import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useFormikContext } from 'formik';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { useCurrentCapacityTypeInfo } from '~hooks/useCurrentCapacityTypeInfo';
import { getDescriptionCapacity } from '~utils/capacityTypes';
import { validateImportErrorsInOrders } from '~utils/order';
import { replaceParamsPath } from '~utils/router';
import { PATHS } from '~constants/paths';
import RouteErrorsList from '~components/RouteErrorsList';
import AccessFeature from '~components/AccessFeature';

import { MAX_SUPPORT_GENERATE_ITEMS } from '../../../utils/constants';
import { validatePresenceOfSkillsOfItemsInVehicle } from '../../../utils/helpers';
import { SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME } from '../../../utils/scheduleRouteForm';

import { AssistantGenerateRoutesDialogByItemsFormGenerateSubmitType } from '../../types';
import { useAssistantGenerateRoutesDialogByItemsContext } from '../../Context';
import {
  FORM_BY_ITEMS_FIELDS_NAME,
  getFormByItemsInitialValues,
  isSubmiTypeGenerateTemplate,
} from '../../utils';

import { AssistantGenerateRoutesDialogByItemsContentActionsContainer } from './styles';

const AssistantGenerateRoutesDialogByItemsContentActions = (): ReactElement => {
  const navigate = useNavigate();

  const { unit1CapacityTypeId, unit2CapacityTypeId } =
    useCurrentCapacityTypeInfo();

  const {
    onClose,
    activeTab: { tab: currentTab },
    form: {
      selection: {
        orders: {
          selectedRowsIds: selectedItemsId,
          selectedRows: selectedItems,
        },
        vehicles: {
          selectedRowsIds: selectedVehiclesIds,
          selectedRows: selectedVehicles,
        },
      },
      chargeIndicators: {
        orders: indicatorsItemsCharge,
        vehicles: indicatorsVehiclesCharge,
      },
    },
    stepper: {
      currentApiTransactionId: { value: currentApiTransactionId },
      currentRoutes: { value: currentRoutes },
      isSuccesCompleted: { value: isSuccesCompleted },
    },
  } = useAssistantGenerateRoutesDialogByItemsContext();

  const { values, setFieldValue, validateForm, submitForm, isSubmitting } =
    useFormikContext<ReturnType<typeof getFormByItemsInitialValues>>();

  const isTabForm = useMemo(() => currentTab === 'form', [currentTab]);

  const submitType = useMemo(
    () => values[FORM_BY_ITEMS_FIELDS_NAME.SUBMIT_TYPE],
    [values],
  );

  const isTemplate = useMemo(
    () => isSubmiTypeGenerateTemplate(submitType),
    [submitType],
  );

  const isScheduleRouteEnabled = useMemo(
    () => values[SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED],
    [values],
  );

  const alertsRoutes = useMemo(() => {
    const alerts: string[] = [];

    if (selectedVehiclesIds.length > 0) {
      if (
        indicatorsItemsCharge.capacity1 > indicatorsVehiclesCharge.capacity1
      ) {
        alerts.push(
          `Capacidad ${getDescriptionCapacity(
            unit1CapacityTypeId,
          )} de vehículos excedida.`,
        );
      }

      if (
        indicatorsItemsCharge.capacity2 > indicatorsVehiclesCharge.capacity2
      ) {
        alerts.push(
          `Capacidad ${getDescriptionCapacity(
            unit2CapacityTypeId,
          )} de vehículos excedida.`,
        );
      }

      if (selectedItemsId.length > indicatorsVehiclesCharge.maximumVisits) {
        alerts.push('Cantidad máxima de paradas de los vehículos excedida.');
      }

      if (
        !validatePresenceOfSkillsOfItemsInVehicle(
          selectedItems,
          selectedVehicles,
        )
      ) {
        alerts.push(
          'No se seleccionaron vehiculos para todos los tipos de cargas especiales a programar.',
        );
      }

      if (selectedItemsId.length > MAX_SUPPORT_GENERATE_ITEMS) {
        alerts.push(
          `Se supera el límite de ${MAX_SUPPORT_GENERATE_ITEMS} pedidos por rutas.`,
        );
      }
    }

    if (validateImportErrorsInOrders(selectedItems)) {
      alerts.push(
        // eslint-disable-next-line max-len
        `Algunos pedidos contienen errores y no se podran incluir en las generación de rutas`,
      );
    }

    return alerts;
  }, [
    indicatorsItemsCharge,
    indicatorsVehiclesCharge,
    selectedItems,
    selectedItemsId.length,
    selectedVehicles,
    selectedVehiclesIds.length,
    unit1CapacityTypeId,
    unit2CapacityTypeId,
  ]);

  const handleSubmitByType = useCallback(
    async (
      type: AssistantGenerateRoutesDialogByItemsFormGenerateSubmitType,
    ) => {
      setFieldValue(FORM_BY_ITEMS_FIELDS_NAME.SUBMIT_TYPE, type);

      await validateForm();

      await submitForm();
    },
    [setFieldValue, validateForm, submitForm],
  );

  const disabledSubmitBase = useMemo(
    () => !selectedItemsId.length || isSubmitting,
    [selectedItemsId, isSubmitting],
  );

  const disabledSubmitSaveTemplate = useMemo(
    () => disabledSubmitBase || selectedVehiclesIds.length !== 1,
    [disabledSubmitBase, selectedVehiclesIds],
  );

  const disabledSubmitGenerateRoute = useMemo(
    () => disabledSubmitBase || !selectedVehiclesIds.length,
    [disabledSubmitBase, selectedVehiclesIds],
  );

  const getLoadingSubmit = useCallback(
    (type: AssistantGenerateRoutesDialogByItemsFormGenerateSubmitType) =>
      isSubmitting && submitType === type,
    [isSubmitting, submitType],
  );

  const handleRedirectRouteDetails = useCallback(() => {
    if (currentApiTransactionId) {
      const urlRedirect = replaceParamsPath(PATHS.ROUTES.VEHICLE_PLANNING, {
        ':apiTransactionId': currentApiTransactionId,
        ':routeId': currentRoutes[0].routeId,
      });

      navigate(urlRedirect);
    }
  }, [currentApiTransactionId, currentRoutes, navigate]);

  const handleRedirectRoutesList = useCallback(() => {
    navigate(PATHS.ROUTES.BASE);
  }, [navigate]);

  return (
    <AssistantGenerateRoutesDialogByItemsContentActionsContainer>
      <Stack flex={1}>
        {isTabForm && (
          <RouteErrorsList
            title="Posibles errores encontrados"
            errors={alertsRoutes}
          />
        )}

        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button color="secondary" onClick={onClose}>
            Cerrar
          </Button>

          {isTabForm && (
            <Stack direction="row" spacing={2}>
              {!isScheduleRouteEnabled && (
                <AccessFeature.Button
                  validFeature={PlanFeaturesTypes.MgmRepeatRoutes}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleSubmitByType('template')}
                  loading={getLoadingSubmit('template')}
                  disabled={disabledSubmitSaveTemplate}
                >
                  Guardar como plantilla...
                </AccessFeature.Button>
              )}

              <LoadingButton
                variant="contained"
                color="primary"
                onClick={() => handleSubmitByType('route')}
                loading={getLoadingSubmit('route')}
                disabled={disabledSubmitGenerateRoute}
              >
                Generar
              </LoadingButton>
            </Stack>
          )}

          {!isTabForm && isSuccesCompleted && (
            <Stack direction="row" spacing={2}>
              {(isTemplate || isScheduleRouteEnabled) && (
                <Button
                  variant={isScheduleRouteEnabled ? 'outlined' : 'contained'}
                  color="primary"
                  onClick={handleRedirectRouteDetails}
                >
                  Ver plantilla
                </Button>
              )}

              {!isTemplate && !isScheduleRouteEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRedirectRouteDetails}
                >
                  Ver ruta
                </Button>
              )}

              {isScheduleRouteEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRedirectRoutesList}
                >
                  Ver listado de rutas
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </AssistantGenerateRoutesDialogByItemsContentActionsContainer>
  );
};

export default AssistantGenerateRoutesDialogByItemsContentActions;
